import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

const TOKEN_KEY = 'app-token';
const UID_KEY = 'uid';
const USER_INFO_KEY = 'user-info';
const ROLE_KEY = 'app-role';
const ONBOARDING_STATUS_KEY = 'app-onboarding-status';

/**
 * 认证 & 缓存服务
 *
 * 对应 study-plan 项目 `utils/cache.ts` 的核心能力：
 * - Token（用于 Authorization Header）
 * - UID（用户唯一标识）
 * - 用户信息存取
 * - 清除登录态
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  // ==========================================
  //  Token
  // ==========================================

  /** 设置 token */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /** 获取 token */
  getToken(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  /** 清除 token */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  // ==========================================
  //  UID
  // ==========================================

  /** 设置 UID */
  setUID(uid: string): void {
    localStorage.setItem(UID_KEY, uid);
  }

  /** 获取 UID */
  getUID(): string {
    return localStorage.getItem(UID_KEY) || '';
  }

  /** 清除 UID */
  clearUID(): void {
    localStorage.removeItem(UID_KEY);
  }

  // ==========================================
  //  用户信息
  // ==========================================

  /** 设置用户信息 */
  setUserInfo(userInfo: Record<string, unknown>): void {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }

  /** 获取用户信息 */
  getUserInfo(): Record<string, unknown> | null {
    const raw = localStorage.getItem(USER_INFO_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** 清除用户信息 */
  clearUserInfo(): void {
    localStorage.removeItem(USER_INFO_KEY);
  }

  // ==========================================
  //  角色 & 入驻状态
  // ==========================================

  /** 设置角色 */
  setRole(role: string): void {
    localStorage.setItem(ROLE_KEY, role);
  }

  /** 获取角色 */
  getRole(): string {
    return localStorage.getItem(ROLE_KEY) || '';
  }

  /** 清除角色 */
  clearRole(): void {
    localStorage.removeItem(ROLE_KEY);
  }

  /** 设置入驻状态 */
  setOnboardingStatus(status: string): void {
    localStorage.setItem(ONBOARDING_STATUS_KEY, status);
  }

  /** 获取入驻状态 */
  getOnboardingStatus(): string {
    return localStorage.getItem(ONBOARDING_STATUS_KEY) || '';
  }

  /** 清除入驻状态 */
  clearOnboardingStatus(): void {
    localStorage.removeItem(ONBOARDING_STATUS_KEY);
  }

  // ==========================================
  //  是否已登录
  // ==========================================

  get isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // ==========================================
  //  清除所有
  // ==========================================

  // ==========================================
  //  登出通知（应用层订阅后统一处理跳转）
  // ==========================================

  /**
   * 当登录态被清除时（401 / 主动退出），发出通知。
   * 由应用层（如 AppComponent）订阅，负责跳转逻辑。
   */
  readonly loggedOut$ = new Subject<void>();

  /** 清除所有登录态（用于退出登录 / 401 时），并通知应用层 */
  clearAll(): void {
    this.clearToken();
    this.clearUID();
    this.clearUserInfo();
    this.clearRole();
    this.clearOnboardingStatus();
    this.loggedOut$.next();
  }
}
